/* eslint-disable */
import React, { useMemo, useRef } from 'react';
import { useShellStore } from './shellStore';
import { useFitBar } from './useFitBar';
import { Ico } from './ShellIcons';
import { BrandMark } from './BrandMark';
import { Identity, StatusSlot } from './Identity';
import FilterField from './FilterField';
import { SegField, ChipsField, ToggleField } from './InlineFields';
import MoreFilters from './MoreFilters';
import Rail from './Rail';
import StageSpectrum from './StageSpectrum';
import PortalSearch from './PortalSearch';
import { useWide } from './useWide';
import { MenuControl, ViewsMenu, DensityToggle, ActionButton, PrimaryButton, AuxButton } from './BarControls';
import fb from './filterBar.module.css';
import sh from './shell.module.css';

// ============================================================
// FilterBar — row 2 of Shell v3, the same grammar in every portal:
//   identity › section · status · FILTERS … · search · group · sort · views ·
//   density · actions · primary · aux
// It is a pure composer: every portal hands it a config (see DevsFilterBar)
// and the bar renders, measures and collapses itself (useFitBar). The one
// open popover is the shell store's `open` key, closed by a document click
// or Escape in ShellRoot.
// ============================================================

export default function FilterBar({ cfg, condensed, deadlineCount, notifCount, onOpenDeadlines, onOpenNotifications, onOpenMenu }) {
  const barRef = useRef(null);
  const open = useShellStore((s) => s.open);
  const railH = useShellStore((s) => s.railH);
  const toggleOpen = useShellStore((s) => s.toggleOpen);
  const closeAll = useShellStore((s) => s.closeAll);
  useFitBar(barRef, fb);

  const wide = useWide(600);
  const searchLeft = !!cfg.searchLeft && wide;
  const fields = cfg.fields || [];
  const activeCount = useMemo(() => fields.reduce((n, f) => {
    if (f.type === 'toggle') return n + (f.value && f.countsAsFilter !== false ? 1 : 0);
    if (f.type === 'seg' || f.type === 'chips') return n + (f.value != null && f.value !== f.defaultValue && f.countsAsFilter !== false && f.defaultValue !== undefined ? 1 : 0);
    if (f.type === 'spec') return n + (f.value && f.value.size ? 1 : 0);
    return n + (f.value && f.value.size ? 1 : 0);
  }, 0) + (cfg.search && cfg.search.value ? 1 : 0), [fields, cfg.search && cfg.search.value]);

  const openKey = (k) => `filter:${k}`;
  let listIdx = 0;
  // `data-shell-bar` is how the pins find the chrome's bottom edge (the Orders
  // Table, the QC Data pin, the Orders focus). Never give this bar
  // `data-app-header`: AppShell.module.css sizes that attribute to the whole
  // chrome height.
  // cfg.rail (CLB-064) — the third row: SCOPE · FILTERS · RESULT under the bar.
  return (
    <>
    <div ref={barRef} className={`${fb.fbar} ${condensed ? fb.cond : ''} ${railH > 0 ? fb.hasRail : ''}`} data-shell-bar role="toolbar" aria-label={`${cfg.portal.name} filters`}>
      <div className={fb.miniBrand}><BrandMark className={fb.mark} /></div>
      <Identity
        portal={cfg.portal} worlds={cfg.worlds} world={cfg.world} onWorld={cfg.onWorld}
        sections={cfg.sections} section={cfg.section} onSection={cfg.onSection} worldCounts={cfg.worldCounts} onPickIn={cfg.onPickIn} count={cfg.count}
        open={open === 'menu:sections'} onToggle={() => toggleOpen('menu:sections')} onClose={closeAll}
      />
      <StatusSlot items={cfg.status} />
      <span className={`${fb.vdiv} ${fb.v2}`} />
      <div className={fb.ffs} data-ffs>
        {fields.map((f) => {
          const k = openKey(f.key);
          if (f.type === 'spec') return <StageSpectrum key={f.key} {...f.spec} value={f.value} onToggleKey={f.onToggleKey} onClear={f.onClear} open={open === k} onToggle={() => toggleOpen(k)} onClose={closeAll} />;
          if (f.type === 'seg') return <SegField key={f.key} field={f} open={open === k} onToggle={() => toggleOpen(k)} />;
          if (f.type === 'chips') return <ChipsField key={f.key} field={f} open={open === k} onToggle={() => toggleOpen(k)} />;
          if (f.type === 'toggle') return <ToggleField key={f.key} field={f} />;
          if (f.type === 'custom') return <div key={f.key} className={fb.ffw} data-ffw="custom">{f.render()}</div>;
          listIdx += 1;
          return <FilterField key={f.key} field={{ ...f, priority: f.priority || listIdx }} open={open === k} onToggle={() => toggleOpen(k)} onClose={closeAll} />;
        })}
        {cfg.search && searchLeft && <div className={fb.searchL} data-search-left><PortalSearch scope={cfg.search.scope || cfg.portal.key} value={cfg.search.value} onChange={cfg.search.onChange} placeholder={cfg.search.placeholder} portalName={cfg.portal.name} filterBy={cfg.search.filterBy} onFilterBy={(key) => { if (key === '__search') return; toggleOpen(openKey(key)); }} inputRef={cfg.search.inputRef} /></div>}
        {fields.length > 0 && (
          <MoreFilters fields={fields} open={open === 'filter:more'} onToggle={() => toggleOpen('filter:more')} onClose={closeAll} onClearAll={() => { cfg.onClearAll && cfg.onClearAll(); closeAll(); }} activeCount={activeCount} />
        )}
        {activeCount > 0 && cfg.onClearAll && (
          <button type="button" className={fb.ffClear} onClick={(e) => { e.stopPropagation(); cfg.onClearAll(); }} title="Clear every filter"><Ico name="x" />Clear {activeCount}</button>
        )}
      </div>
      <div className={fb.tools2}>
        {cfg.search && !searchLeft && <PortalSearch scope={cfg.search.scope || cfg.portal.key} value={cfg.search.value} onChange={cfg.search.onChange} placeholder={cfg.search.placeholder} portalName={cfg.portal.name} filterBy={cfg.search.filterBy} onFilterBy={(key) => { if (key === '__search') return; toggleOpen(openKey(key)); }} inputRef={cfg.search.inputRef} />}
        {cfg.group && <MenuControl id="group" icon="layers" small="Group" kind="groupBtn" value={cfg.group.value} items={cfg.group.items} onPick={(k) => { cfg.group.onPick(k); closeAll(); }} open={open === 'menu:group'} onToggle={() => toggleOpen('menu:group')} />}
        {cfg.sort && <MenuControl id="sort" icon="sort" small="Sort" kind="sortBtn" value={cfg.sort.value} items={cfg.sort.items} onPick={(k) => { cfg.sort.onPick(k); closeAll(); }} open={open === 'menu:sort'} onToggle={() => toggleOpen('menu:sort')} descending={cfg.sort.descending} onToggleDir={cfg.sort.onToggleDir} />}
        {cfg.views && <ViewsMenu views={cfg.views.list} onApply={(v) => { cfg.views.onApply(v); closeAll(); }} onSave={cfg.views.onSave} onDelete={cfg.views.onDelete} open={open === 'menu:views'} onToggle={() => toggleOpen('menu:views')} label={cfg.views.label} count={cfg.views.count} iconOnly={cfg.views.iconOnly} />}
        {cfg.density && <DensityToggle value={cfg.density.value} onChange={cfg.density.onChange} />}
        {(cfg.tools || []).map((t, i) => <span key={t.key || i} className={fb.toolNode} data-bar-node={t.key || ''}>{t.node}</span>)}
        {(cfg.actions || []).map((a) => <ActionButton key={a.key || a.label} icon={a.icon} label={a.label} onClick={a.onClick} title={a.title} disabled={a.disabled} />)}
        {cfg.primary && <PrimaryButton label={cfg.primary.label} onClick={cfg.primary.onClick} icon={cfg.primary.icon} title={cfg.primary.title} disabled={cfg.primary.disabled} />}
        {(cfg.aux || []).map((a) => <AuxButton key={a.key || a.title} icon={a.icon} title={a.title} onClick={a.onClick} on={a.on} hideOnTouch={a.hideOnTouch} />)}
      </div>
      <div className={fb.miniRight}>
        <button type="button" className={`${sh.iconBtn} ${sh.mini}`} onClick={(e) => { e.stopPropagation(); onOpenDeadlines(); }} aria-label="Deadlines"><Ico name="clock" />{deadlineCount > 0 && <span className={`${sh.badge} ${sh.red}`}>{deadlineCount > 99 ? '99+' : deadlineCount}</span>}</button>
        <button type="button" className={`${sh.iconBtn} ${sh.mini}`} onClick={(e) => { e.stopPropagation(); onOpenNotifications(); }} aria-label="Notifications"><Ico name="bell" />{notifCount > 0 && <span className={sh.badge}>{notifCount > 9 ? '9+' : notifCount}</span>}</button>
        <button type="button" className={`${sh.iconBtn} ${sh.mini}`} onClick={(e) => { e.stopPropagation(); onOpenMenu(); }} aria-label="Menu"><Ico name="menu" /></button>
      </div>
    </div>
    {cfg.rail ? <Rail cfg={cfg.rail} /> : null}
    {cfg.tray || null}
    </>
  );
}
